import { useState } from 'react'

/**
 * Lignes cochées, pour l'affectation en masse à un groupe. L'appelant la vide
 * à chaque changement de page, de taille de page, de recherche ou de filtre :
 * agir sur une sélection qu'on ne voit plus à l'écran est le meilleur moyen de
 * rattacher les mauvais apprenants.
 *
 * Les lignes de la page se passent à chaque appel plutôt qu'à la création :
 * elles viennent d'une requête qui dépend des filtres, eux-mêmes chargés de
 * vider la sélection.
 */
export function useUserSelection() {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set())

  const isPageSelected = (pageIds: readonly string[]) =>
    pageIds.length > 0 && pageIds.every(id => selectedIds.has(id))

  const toggleRow = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const togglePage = (pageIds: readonly string[]) => {
    const deselect = isPageSelected(pageIds)
    setSelectedIds(current => (
      deselect
        ? new Set([...current].filter(id => !pageIds.includes(id)))
        : new Set([...current, ...pageIds])
    ))
  }

  const clear = () => setSelectedIds(new Set())

  return { selectedIds, isPageSelected, toggleRow, togglePage, clear }
}

export type UserSelection = ReturnType<typeof useUserSelection>
